import clsx from "clsx";
import type { ReactNode } from "react";
import type { CurrencyPrimitive } from "@/api/cashier";
import {
  AmountBox,
  type AmountBoxProps,
} from "@/components/amount-box/AmountBox";
import { CurrencySelect } from "@/components/currency/currency-select/CurrencySelect";
import { ErrorMessage } from "@/components/ErrorMessage";
import style from "./ExchangerInput.module.css";
import { Icon } from "@/components/general/icon/Icon";
import type { EIcon } from "@/utils/enums";
import globe from "@/assets/icons/globe-icon.svg";

type ExchangerInputProps = Omit<AmountBoxProps, "children" | "currency"> & {
  selectedCurrency: CurrencyPrimitive | undefined;
  processingCurrencies?: CurrencyPrimitive[];
  setSelectedCurrency?: (currency: CurrencyPrimitive) => void;
  topLabel?: ReactNode;
  topContent?: ReactNode;
  locale: string;
  errorMessage?: string;
  mobileTrigger?: ReactNode;
};

export const ExchangerInput = ({
  label,
  subText,
  placeholder,
  value,
  disabled = false,
  testId,
  hasError,
  onChange,
  selectedCurrency,
  processingCurrencies,
  setSelectedCurrency,
  topLabel,
  topContent,
  errorMessage,
  locale,
  mobileTrigger,
}: ExchangerInputProps) => {
  const showDropdown =
    processingCurrencies &&
    processingCurrencies.length > 1 &&
    setSelectedCurrency;

  const showSingleCurrency =
    (!processingCurrencies || processingCurrencies.length <= 1) &&
    selectedCurrency;

  return (
    <div
      className={clsx(
        hasError ? style.withError : "",
        mobileTrigger && style.mobile,
        hasError && mobileTrigger ? style.withErrorMobile : ""
      )}
    >
      <div className={clsx(mobileTrigger && style.withMobileTrigger)}>
        {(topLabel || topContent) && (
          <div className={style["amount-top-content"]}>
            {Boolean(topLabel) && (
              <p className={style["amount-top-label"]}>{topLabel}</p>
            )}
            {Boolean(topContent) && topContent}
          </div>
        )}
        {mobileTrigger}
      </div>
      <AmountBox
        label={label}
        subText={subText}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        currency={selectedCurrency}
        testId={testId}
        hasError={hasError || Boolean(errorMessage)}
        onChange={onChange}
        locale={locale}
        isMobile={!!mobileTrigger}
      >
        {showDropdown && (
          <div
            className={style["select-currency"]}
            data-testid="amount-box-currency-select-wrapper"
          >
            <CurrencySelect
              testId={`${testId}-amount-box-currency-select`}
              currencies={processingCurrencies}
              selectedCurrency={selectedCurrency || processingCurrencies[0]}
              setSelectedCurrency={setSelectedCurrency}
              disabled={disabled}
            />
          </div>
        )}
        {showSingleCurrency && (
          <div
            data-testid={`${testId}-amount-box-single-currency`}
            className={`cursor-pointer ${style["single-currency"]}`}
          >
            <Icon
              name={
                `flags/${selectedCurrency.code?.toLowerCase()}-flag` as EIcon
              }
              fallback={globe}
            />
            <span>{selectedCurrency.code}</span>
          </div>
        )}
      </AmountBox>
      {errorMessage && (
        <ErrorMessage
          className={style["error-message"]}
          message={errorMessage}
        />
      )}
    </div>
  );
};
